import axios from 'axios';
import config from '../config';
import selectedEnvironment from '../env';
const API_URL = `${config[selectedEnvironment].API_URL}/UserTypePermissions`;
const RESTRICTIONS_API_URL = `${config[selectedEnvironment].API_URL}/SubAccountantCaseRestrictions`;

const checkPermission = async (userId,userType ,PermissionName,token) => {
    try {
        const response = await fetch(`${API_URL}/CheckPermission`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ userId,userType ,PermissionName }),
        });

        if (!response.ok) {
            throw new Error("Permission check failed");
        }

        const { hasPermission } = await response.json();
        return hasPermission;
    } catch (error) {
        console.error("Error checking permission:", error);
        return false;
    }
};

const checkPermissionById = async (userId,userType ,PermissionId,token) => {
    try {
        const response = await fetch(`${API_URL}/CheckPermissionById`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ userId,userType ,PermissionId }),
        });

        if (!response.ok) {
            throw new Error("Permission check failed");
        }

        const { hasPermission } = await response.json();
        return hasPermission;
    } catch (error) {
        console.error("Error checking permission:", error);
        return false;
    }
};

const GetPermissions = async (userId,userType ,token) => {
    try {
        const response = await fetch(`${API_URL}/GetPermissionsByUserId`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ userId,userType  }),
        });

        if (!response.ok) {
            throw new Error("Permission check failed");
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error("Error checking permission:", error);
        return false;
    }
};


const assignPermission = async (userId,userType,permissionId ,token) => {
    try {
        const response = await fetch(`${API_URL}/AssignPermission`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ userId,userType ,permissionId }),
        });

        if (!response.ok) {
            throw new Error("Permission check failed");
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error("Error checking permission:", error);
        return false;
    }
};


const removePermission = async (userId,userType,permissionId ,token) => {
    try {
        const response = await fetch(`${API_URL}/RemovePermission`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ userId,userType,permissionId  }),
        });

        if (!response.ok) {
            throw new Error("Permission check failed");
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error("Error checking permission:", error);
        return false;
    }
};

const GetPermissionsByUserId = async (userId, userType, token) => {
    try {
        const response = await fetch(`${API_URL}/GetPermissionsByUserId`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ userId, userType }),
        });

        if (!response.ok) {
            throw new Error("Failed to fetch user permissions");
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error("Error fetching user permissions:", error);
        return [];
    }
};

const AssignPermission = async (userId, userType, permissionId, token) => {
    return await assignPermission(userId, userType, permissionId, token);
};

const RemovePermission = async (userId, userType, permissionId, token) => {
    return await removePermission(userId, userType, permissionId, token);
};

/**
 * Get restriction keys for the current SubAccountant and optional caseId.
 * When caseId is null/empty, returns global (all-clients) restrictions.
 * @param {string|null} caseId - Client case id or null for "all clients"
 * @param {string} token
 * @returns {Promise<string[]>} List of restriction keys (denied)
 */
const GetMyRestrictionsForCase = async (caseId, token) => {
    try {
        const response = await fetch(`${RESTRICTIONS_API_URL}/GetMyRestrictionsForCase`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ caseId: caseId || null }),
        });
        if (!response.ok) throw new Error('Failed to fetch restrictions');
        const data = await response.json();
        return Array.isArray(data) ? data : [];
    } catch (error) {
        console.error('Error fetching SubAccountant restrictions:', error);
        return [];
    }
};

/**
 * Get all restriction key definitions from DB (categories and items) for the restrictions editor.
 * @param {string} token
 * @returns {Promise<Array<{ key: string, categoryKey: string|null, displayName: string, sortOrder: number }>>}
 */
const GetRestrictionKeyDefinitions = async (token) => {
    try {
        const response = await fetch(`${RESTRICTIONS_API_URL}/KeyDefinitions`, {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        if (!response.ok) throw new Error('Failed to fetch key definitions');
        const data = await response.json();
        return Array.isArray(data) ? data : [];
    } catch (error) {
        console.error('Error fetching restriction key definitions:', error);
        return [];
    }
};

/**
 * Add or update a single restriction key definition in the DB. Admin/Accountant only.
 * @param {{ key: string, categoryKey?: string|null, displayName: string, sortOrder: number }} payload
 * @param {string} token
 */
const AddRestrictionKeyDefinition = async (payload, token) => {
    const response = await fetch(`${RESTRICTIONS_API_URL}/KeyDefinitions`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
            key: payload.key,
            categoryKey: payload.categoryKey ?? null,
            displayName: payload.displayName ?? payload.key,
            sortOrder: payload.sortOrder ?? 0,
        }),
    });
    if (!response.ok) throw new Error('Failed to add or update key definition');
    return response.json();
};

/**
 * Sync restriction key definitions from frontend (menuRestrictionKeys.js) to DB. Adds missing keys, updates existing. Admin/Accountant only.
 * Use buildRestrictionKeysSyncPayload() from Utils/restrictionKeysSync.js to build the payload.
 * @param {{ definitions: Array<{ key: string, categoryKey?: string|null, displayName: string, sortOrder: number }> }} payload
 * @param {string} token
 */
const SyncRestrictionKeyDefinitions = async (payload, token) => {
    const response = await fetch(`${RESTRICTIONS_API_URL}/KeyDefinitions/Sync`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
            definitions: Array.isArray(payload.definitions) ? payload.definitions : [],
        }),
    });
    if (!response.ok) throw new Error('Failed to sync key definitions');
    return response.json();
};

/**
 * Get restriction keys for a subAccountant and optional case (admin/accountant).
 * @param {string} subAccountantId
 * @param {string|null} caseId - null = all restrictions (all clients); set = global + that case only
 * @param {string} token
 */
const GetRestrictionsBySubAndCase = async (subAccountantId, caseId, token) => {
    try {
        const response = await fetch(`${RESTRICTIONS_API_URL}/GetBySubAndCase`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
                SubAccountantId: String(subAccountantId),
                CaseId: caseId == null || caseId === '' ? null : String(caseId),
            }),
        });
        if (!response.ok) throw new Error('Failed to fetch restrictions');
        const data = await response.json();
        return Array.isArray(data) ? data : [];
    } catch (error) {
        console.error('Error fetching SubAccountant restrictions:', error);
        return [];
    }
};

/**
 * Get restriction keys for multiple subAccountants in one request (admin/accountant).
 * @param {string[]} subAccountantIds
 * @param {string|null} caseId - null = all restrictions (all clients) per sub-accountant; set = global + that case only
 * @param {string} token
 * @returns {Promise<Record<string, string[]>>} Map of subAccountantId -> array of restriction keys
 */
const GetRestrictionsBySubAccountantIds = async (subAccountantIds, caseId, token) => {
    try {
        if (!Array.isArray(subAccountantIds) || subAccountantIds.length === 0)
            return {};
        const ids = subAccountantIds.map((id) => (id != null ? String(id) : '')).filter(Boolean);
        if (ids.length === 0) return {};
        const response = await fetch(`${RESTRICTIONS_API_URL}/GetBySubAccountantIds`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
                subAccountantIds: ids,
                caseId: caseId == null || caseId === '' ? null : String(caseId),
            }),
        });
        if (!response.ok) throw new Error('Failed to fetch restrictions');
        const data = await response.json();
        if (!data || typeof data !== 'object') return {};
        const result = {};
        for (const id of ids) {
            const keys = data[id] ?? data[String(id)] ?? [];
            result[id] = Array.isArray(keys) ? keys : [];
        }
        return result;
    } catch (error) {
        console.error('Error fetching SubAccountant restrictions (batch):', error);
        return {};
    }
};

/**
 * Get all restrictions for a sub-accountant grouped by case. Returns { "": string[], "caseId1": string[], ... }. Key "" = global (all clients).
 * @param {string} subAccountantId
 * @param {string} token
 * @returns {Promise<Record<string, string[]>>}
 */
const GetRestrictionsBySubAccountantGroupedByCase = async (subAccountantId, token) => {
    try {
        if (subAccountantId == null || String(subAccountantId).trim() === '') return {};
        const id = String(subAccountantId).trim();
        const response = await fetch(`${RESTRICTIONS_API_URL}/BySubAccountant/${encodeURIComponent(id)}/GroupedByCase`, {
            method: 'GET',
            headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error('Failed to fetch restrictions');
        const data = await response.json();
        if (!data || typeof data !== 'object') return {};
        const result = {};
        for (const [key, arr] of Object.entries(data)) {
            result[key] = Array.isArray(arr) ? arr : [];
        }
        return result;
    } catch (error) {
        console.error('Error fetching restrictions grouped by case:', error);
        return {};
    }
};

/**
 * Bulk set restrictions for (subAccountantId, caseId) or for many cases in one request.
 * caseId null = all clients. When caseIds is a non-empty array, the same restrictions apply to each case (single API round-trip).
 * @param {object} payload - { subAccountantId, caseId?, caseIds?, restrictions: [{ restrictionKey, isDenied }] }
 * @param {string} token
 */
const BulkSetSubAccountantRestrictions = async (payload, token) => {
    const restrictions = Array.isArray(payload.restrictions)
        ? payload.restrictions.map((r) => ({ RestrictionKey: r.restrictionKey, IsDenied: r.isDenied !== false }))
        : [];
    const caseIds = Array.isArray(payload.caseIds)
        ? payload.caseIds.map((id) => String(id)).filter((id) => id !== '')
        : [];
    const body = {
        SubAccountantId: payload.subAccountantId != null ? String(payload.subAccountantId) : '',
        Restrictions: restrictions,
    };
    if (caseIds.length > 0) {
        body.CaseIds = caseIds;
    } else {
        body.CaseId = payload.caseId == null || payload.caseId === '' ? null : String(payload.caseId);
    }
    const response = await fetch(`${RESTRICTIONS_API_URL}/BulkSet`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error('Failed to update restrictions');
    return response.json();
};

const PermissionService = {
    checkPermission,
    GetPermissions,
    assignPermission,
    removePermission,
    checkPermissionById,
    GetPermissionsByUserId,
    AssignPermission,
    RemovePermission,
    GetMyRestrictionsForCase,
    GetRestrictionsBySubAndCase,
    GetRestrictionsBySubAccountantIds,
    GetRestrictionsBySubAccountantGroupedByCase,
    GetRestrictionKeyDefinitions,
    AddRestrictionKeyDefinition,
    SyncRestrictionKeyDefinitions,
    BulkSetSubAccountantRestrictions,
};

export default PermissionService;
